// apps/cli/src/commands/enterprise/campaigns/update.ts
import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePatch } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { printJson } from '../../../lib/output';
import { requireEncId, type WriteCtx } from '../jobs/write-shared';
import type { CreatedCampaign } from './create';

interface UpdateFlags {
	file?: string;
	idempotencyKey?: string;
}

/**
 * `campaigns update` 核心：讀 --file/stdin JSON → PATCH /campaigns/:enc_id（200）。
 * body 直送後端 UpdateCampaignDto 驗證；enc_job_ids 由後端解密。
 */
export async function runCampaignUpdate(
	ctx: WriteCtx,
	apiKey: string,
	encId: string,
	source: string,
	idempotencyKey: string
): Promise<void> {
	const trimmed = requireEncId(encId);
	const campaignBody = readJsonObject(source, { timeoutMs: ctx.timeoutMs });
	const { body } = await enterprisePatch(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/campaigns/${encodeURIComponent(trimmed)}`,
		campaignBody,
		{ idempotencyKey }
	);
	const updated = unwrapDataResponse<CreatedCampaign>(body);
	if (ctx.format === 'json') {
		printJson(updated);
		return;
	}
	process.stdout.write(`Updated campaign: ${updated.enc_id ?? trimmed}\n`);
}

export function registerEnterpriseCampaignsUpdate(parent: Command): void {
	parent
		.command('update <enc_id>')
		.description('Update a recruitment campaign from a JSON file (use "-" to read stdin)')
		.requiredOption('--file <path>', 'path to a partial JSON campaign body, or "-" for stdin')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: UpdateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCampaignUpdate(ctx, key, encId, flags.file as string, flags.idempotencyKey ?? randomUUID());
		});
}
